import { useEffect, useState } from "react";
import { Copy, Trash2, ChevronDown, Send, Loader2, CheckCircle2, XCircle, Clock, History, CalendarDays, ChevronLeft, ChevronRight, Plus, CalendarPlus, Repeat } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PublishDialog from "@/components/admin/PublishDialog";
import NewScheduleDialog from "@/components/admin/NewScheduleDialog";

const CHANNELS = [
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "tiktok", label: "TikTok" },
  { key: "blog", label: "Blog" },
];

export default function ContentHistory({ businesses, role }) {
  const canEdit = role !== "operator";
  const [contents, setContents] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [pub, setPub] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(null);
  const [newSchedule, setNewSchedule] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/contents"), api.get("/scheduled-posts")]);
      setContents(c.data);
      setScheduled(s.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/contents"), api.get("/scheduled-posts")]);
      setContents(c.data);
      setScheduled(s.data);
      setSelectedDay((sd) => {
        if (!sd) return sd;
        const k = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return { ...sd, posts: s.data.filter((p) => k(new Date(p.send_at)) === sd.key) };
      });
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Disalin"); };

  const delContent = async (id) => {
    await api.delete(`/contents/${id}`);
    toast.success("Konten dihapus");
    load();
  };

  const delSchedule = async (p) => {
    if (p.series_id) {
      if (window.confirm("Jadwal ini bagian dari seri berulang. Hapus SEMUA jadwal seri yang masih menunggu?\n\nOK = hapus semua seri, Batal = hapus hanya jadwal ini")) {
        await api.delete(`/scheduled-posts/${p.id}`, { params: { series: true } });
        toast.success("Seri jadwal berulang dihapus");
        load();
        return;
      }
    }
    await api.delete(`/scheduled-posts/${p.id}`);
    toast.success("Jadwal dihapus");
    load();
  };

  const markDone = async (p) => {
    try {
      const r = await api.post(`/scheduled-posts/${p.id}/done`);
      toast.success(r.data?.idea_removed ? "Ditandai digarap — ide otomatis tercentang dari favorit" : "Ditandai sudah digarap");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const statusBadge = (st) => {
    if (st === "cancelled") return <span className="text-slate-600 text-xs">Dibatalkan</span>;
    if (st === "processing" || st === "posting") return <span className="flex items-center gap-1 text-blue-600 text-xs"><Loader2 size={13} className="animate-spin" /> Memposting</span>;
    if (st === "posted") return <span className="flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 size={13} /> Terposting</span>;
    if (st === "sent") return <span className="flex items-center gap-1 text-[#059669] text-xs"><CheckCircle2 size={13} /> Terkirim</span>;
    if (st === "done") return <span className="flex items-center gap-1 text-[#3B82F6] text-xs"><CheckCircle2 size={13} /> Digarap</span>;
    if (st === "failed") return <span className="flex items-center gap-1 text-[#E11D48] text-xs"><XCircle size={13} /> Gagal</span>;
    return <span className="flex items-center gap-1 text-[#B45309] text-xs"><Clock size={13} /> Menunggu</span>;
  };

  if (loading) return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat...</div>;

  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const postsByDay = {};
  scheduled.forEach((p) => {
    const d = new Date(p.send_at);
    const k = dayKey(d);
    (postsByDay[k] = postsByDay[k] || []).push(p);
  });
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - ((monthStart.getDay() + 6) % 7)); // Monday-first
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const monthLabel = month.toLocaleString("id-ID", { month: "long", year: "numeric" });
  const todayKey = dayKey(new Date());
  const statusDot = { sent: "#10B981", failed: "#F43F5E", pending: "#F59E0B", done: "#3B82F6" };

  return (
    <div>
      <Tabs defaultValue={role === "operator" ? "scheduled" : "history"}>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <TabsList className="bg-[#F1F5F9] h-auto max-w-full flex-wrap gap-1">
            {role !== "operator" && <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white"><History size={15} className="mr-1" /> Riwayat Konten</TabsTrigger>}
            <TabsTrigger value="scheduled" data-testid="tab-scheduled" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white"><Clock size={15} className="mr-1" /> Jadwal Posting</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white"><CalendarDays size={15} className="mr-1" /> Kalender</TabsTrigger>
          </TabsList>
          <span className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="live-indicator">
            <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> Status diperbarui otomatis
          </span>
        </div>

        <TabsContent value="history">
          {contents.length === 0 ? (
            <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center">
              <History size={32} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-600">Belum ada konten tersimpan. Buat di menu Generate Konten.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contents.map((c) => (
                <div key={c.id} data-testid={`content-row-${c.id}`} className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl min-w-0 overflow-x-auto">
                  <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#F1F5F9]/50 transition-colors" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{c.business_name} · <span className="text-slate-500">{c.mode === "auto" ? "Otomatis" : "Manual"}</span></p>
                      <p className="text-xs text-slate-500 truncate">{c.topic || "Dari data produk"} · tone {c.tone} · {new Date(c.created_at).toLocaleString("id-ID")}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canEdit && <button onClick={(e) => { e.stopPropagation(); delContent(c.id); }} data-testid={`delete-content-${c.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#E11D48] transition-colors"><Trash2 size={15} /></button>}
                      <ChevronDown size={18} className={`text-slate-500 transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
                    </div>
                  </div>
                  {expanded === c.id && (
                    <div className="px-5 pb-5 space-y-3 border-t border-[#E2E8F0] pt-4">
                      {CHANNELS.map((ch) => (
                        <div key={ch.key} className="bg-[#F1F5F9] rounded-xl p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <span className="text-xs font-mono-code uppercase tracking-wider text-slate-500">{ch.label}</span>
                            <div className="flex gap-2">
                              <Button onClick={() => copy(c.outputs[ch.key])} size="sm" variant="outline" className="border-[#E2E8F0] bg-[#FFFFFF] hover:bg-[#E2E8F0] rounded-lg text-xs h-7"><Copy size={12} className="mr-1" /> Copy</Button>
                              {c.business_id && (
                                <Button onClick={() => setPub({ businessId: c.business_id, channel: ch.key, text: c.outputs[ch.key] })} size="sm" className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-lg text-xs h-7" data-testid={`publish-${ch.key}-${c.id}`}><Send size={12} className="mr-1" /> Kirim/Jadwal</Button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">{c.outputs[ch.key]}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="scheduled">
          <div className="flex justify-end mb-3">
            {canEdit && <Button onClick={() => setNewSchedule({ date: new Date() })} size="sm" data-testid="scheduled-add-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-lg text-xs"><Plus size={14} className="mr-1" /> Jadwal Baru</Button>}
          </div>
          {scheduled.length === 0 ? (
            <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center">
              <Clock size={32} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-600">Belum ada konten terjadwal.</p>
            </div>
          ) : (
            <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl min-w-0 overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-medium">Bisnis</th>
                    <th className="px-5 py-3 font-medium">Channel</th>
                    <th className="px-5 py-3 font-medium">Konten</th>
                    <th className="px-5 py-3 font-medium">Waktu Kirim</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {scheduled.map((p) => (
                    <tr key={p.id} data-testid={`schedule-row-${p.id}`} className="border-b border-[#F1F5F9]">
                      <td className="px-5 py-3">{p.business_name}</td>
                      <td className="px-5 py-3 text-slate-600 capitalize">{p.channel || "-"}</td>
                      <td className="px-5 py-3 text-slate-600 max-w-[220px] truncate">{p.text}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">
                        {new Date(p.send_at).toLocaleString("id-ID")}
                        {p.repeat && p.repeat !== "none" && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-[#2563EB]/15 text-[#2563EB]" data-testid={`schedule-repeat-badge-${p.id}`}>
                            <Repeat size={9} /> {p.repeat === "daily" ? "Harian" : "Mingguan"}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">{statusBadge(p.status)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {canEdit && p.idea_id && p.status === "pending" && (
                            <button onClick={() => markDone(p)} title="Tandai sudah digarap (ide tercentang dari favorit)" data-testid={`done-schedule-${p.id}`} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-[#3B82F6]/15 text-[#2563EB] hover:bg-[#3B82F6]/30 transition-colors">
                              <CheckCircle2 size={12} /> Sudah Digarap
                            </button>
                          )}
                          {canEdit ? (
                            <button onClick={() => delSchedule(p)} data-testid={`delete-schedule-${p.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#E11D48] transition-colors"><Trash2 size={15} /></button>
                          ) : (
                            <span className="text-[11px] text-slate-600">Lihat saja</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-5" data-testid="calendar-view">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="calendar-prev" className="p-2 rounded-lg hover:bg-[#F1F5F9] text-slate-600"><ChevronLeft size={18} /></button>
              <h3 className="font-heading font-bold capitalize">{monthLabel}</h3>
              <div className="flex items-center gap-2">
                {canEdit && <Button onClick={() => setNewSchedule({ date: new Date() })} size="sm" data-testid="calendar-add-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-lg text-xs"><Plus size={14} className="mr-1" /> Jadwal Baru</Button>}
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="calendar-next" className="p-2 rounded-lg hover:bg-[#F1F5F9] text-slate-600"><ChevronRight size={18} /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1 text-center text-xs uppercase tracking-wider text-slate-500">
              {["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"].map((d) => <div key={d} className="py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                const inMonth = d.getMonth() === month.getMonth();
                const k = dayKey(d);
                const dayPosts = postsByDay[k] || [];
                return (
                  <div
                    key={i}
                    onClick={() => inMonth && setSelectedDay({ key: k, date: new Date(d), posts: dayPosts })}
                    data-testid={inMonth ? `calendar-day-${k}` : undefined}
                    className={`min-h-[84px] rounded-lg border p-1.5 ${inMonth ? "bg-[#F1F5F9] border-[#E2E8F0] cursor-pointer hover:border-[#3B82F6]" : "bg-transparent border-transparent opacity-40"} ${k === todayKey ? "ring-1 ring-[#2563EB]" : ""} transition-colors`}
                  >
                    <div className="text-xs text-slate-500 mb-1">{d.getDate()}</div>
                    <div className="space-y-1">
                      {dayPosts.slice(0, 3).map((p) => (
                        <div key={p.id} title={`${p.channel || "post"} · ${new Date(p.send_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} · ${p.text.slice(0, 40)}`} className="flex items-center gap-1 text-[10px] text-slate-800 bg-[#FFFFFF] rounded px-1 py-0.5 truncate">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusDot[p.status] }} />
                          <span className="truncate">{new Date(p.send_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} {p.channel || ""}</span>
                        </div>
                      ))}
                      {dayPosts.length > 3 && <div className="text-[10px] text-slate-500 px-1">+{dayPosts.length - 3} lagi</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> Menunggu</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> Terkirim</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F43F5E]" /> Gagal</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {selectedDay && (
        <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
          <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-lg max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto" data-testid="day-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading capitalize">
                {selectedDay.date.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-xs">
                {selectedDay.posts.length} jadwal posting pada hari ini
              </DialogDescription>
            </DialogHeader>
            {canEdit && <Button onClick={() => setNewSchedule({ date: selectedDay.date })} data-testid="day-add-schedule" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl mb-1"><CalendarPlus size={16} className="mr-1" /> Tambah Jadwal di Hari Ini</Button>}
            {selectedDay.posts.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Tidak ada jadwal pada tanggal ini.</p>
            ) : (
              <div className="space-y-3">
                {selectedDay.posts.sort((a, b) => new Date(a.send_at) - new Date(b.send_at)).map((p) => (
                  <div key={p.id} className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-3" data-testid={`day-post-${p.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono-code text-[#2563EB]">{new Date(p.send_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                        <span className="capitalize text-slate-700">{p.channel || "post"}</span>
                        {statusBadge(p.status)}
                      </div>
                      {canEdit && <button onClick={() => { delSchedule(p); setSelectedDay((s) => ({ ...s, posts: s.posts.filter((x) => x.id !== p.id) })); }} data-testid={`day-delete-${p.id}`} className="p-1.5 rounded-lg hover:bg-[#FFFFFF] text-slate-600 hover:text-[#E11D48] transition-colors"><Trash2 size={15} /></button>}
                    </div>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap max-h-40 overflow-y-auto mb-2">{p.text}</p>
                    <p className="text-[11px] text-slate-500">Tujuan: {(p.targets && p.targets.length ? p.targets : [p.target]).filter(Boolean).join(", ") || "Nomor admin bisnis"} · {p.business_name}</p>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {newSchedule && (
        <NewScheduleDialog
          open={!!newSchedule}
          onClose={() => setNewSchedule(null)}
          presetDate={newSchedule.date}
          businesses={businesses}
          onCreated={() => { load(); setSelectedDay(null); }}
        />
      )}

      {pub && (
        <PublishDialog
          open={!!pub}
          onClose={() => setPub(null)}
          businessId={pub.businessId}
          channel={pub.channel}
          text={pub.text}
          onScheduled={load}
        />
      )}
    </div>
  );
}
